import { Star, Check, X } from 'lucide-react';

export interface ComparisonItem {
  [key: string]: string | number | boolean;
}

interface ComparisonTableProps {
  items: ComparisonItem[];
  columns?: { key: string; label: string; type?: 'text' | 'number' | 'boolean' | 'rating' }[];
  highlightBest?: boolean;
}

export function ComparisonTable({
  items,
  columns,
  highlightBest = false,
}: ComparisonTableProps) {
  const tableColumns = columns || Object.keys(items[0] || {}).map((key) => ({
    key,
    label: key.charAt(0).toUpperCase() + key.slice(1),
    type: 'text' as const,
  }));

  const renderCell = (value: any, type?: string) => {
    if (type === 'boolean') {
      return value ? (
        <Check className="h-5 w-5 text-green-600 mx-auto" />
      ) : (
        <X className="h-5 w-5 text-red-600 mx-auto" />
      );
    }

    if (type === 'rating' && typeof value === 'number') {
      return (
        <div className="flex gap-0.5 justify-center">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              className={`h-4 w-4 ${
                i < value
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-gray-300'
              }`}
            />
          ))}
        </div>
      );
    }

    return value;
  };

  return (
    <div className="overflow-x-auto">
      <div className="rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="w-full">
          <thead>
            <tr>
              {tableColumns.map((col, index) => (
                <th
                  key={index}
                  className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100 first:rounded-tl-lg last:rounded-tr-lg"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900">
            {items.map((item, rowIndex) => (
              <tr
                key={rowIndex}
                className={`hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors ${
                  highlightBest && rowIndex === 0 ? 'bg-indigo-50 dark:bg-indigo-900/30' : ''
                }`}
              >
                {tableColumns.map((col, colIndex) => (
                  <td
                    key={colIndex}
                    className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100"
                  >
                    {renderCell(item[col.key], col.type)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {highlightBest && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          * První řádek zvýrazněn jako nejlepší volba
        </p>
      )}
    </div>
  );
}
